/**
 * Saving bytes the app already holds.
 *
 * **Why a function and not an `<a href>`.** Everything downloadable in Wend sits
 * behind the tenant guard, and a plain link carries no `Authorization` header —
 * the browser would follow it, get a 401, and save the error envelope under a
 * `.xlsx` name. So the bytes are fetched with the access token (`apiBlob`, which
 * shares this app's single-flight refresh) and handed to the browser here. The
 * storage port makes the same point from the other end: it deliberately returns
 * no URL and no local path for a stored object, so there is no link to give.
 *
 * **The object URL is revoked, and the revoke is deferred by one task.** An
 * unrevoked blob URL keeps the whole file alive for the life of the document —
 * a report over a year of ledger is not a small thing to leak — and revoking it
 * in the same turn as the click is unsafe, because the click *starts* a download
 * rather than completing one. One `setTimeout` is the difference between a leak
 * and a file that arrives empty, so both halves are here rather than left to
 * each caller to remember.
 *
 * **The file name comes from the server's own record**, never from anything a
 * person typed: for a report it is `artifact.fileName`, built by
 * `reportFileName` in `@wend/shared`. `download` is an attribute the browser
 * sanitises, but a name assembled in a component is a name that can disagree
 * with the one the sheet inside claims to be.
 */

/**
 * Offers `blob` to the browser as a file called `fileName`.
 *
 * Returns nothing and throws nothing: once the click is dispatched the download
 * belongs to the browser, and there is no callback that says whether a person
 * kept it. A caller that needs to report failure must do so around the *fetch*,
 * which is where a failure can actually be observed.
 */
export function saveBlobAs(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  // A detached anchor's click is not dispatched everywhere, so it is inserted
  // and removed rather than clicked while outside the document.
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();

  // See the header: the click queues the download, so the URL has to outlive
  // this turn of the event loop.
  window.setTimeout(() => {
    anchor.remove();
    URL.revokeObjectURL(url);
  }, 0);
}
